/**
 * §9.4 Sales Enquiry Form — progressive qualification form.
 * Submits to /api/leads with kind:'launch'.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import { toast } from 'sonner'
import {
  Handshake, Mail, Building2, Globe, Phone, Factory, UsersRound, Layers,
  Tag, Clock, MessageCircle, Loader2, CheckCircle2, Sparkles, ShieldCheck, ArrowRight,
} from 'lucide-react'
import {
  Container, Eyebrow, SectionHeading, GradientButton,
} from '@/components/landing/design-system'
import {
  INDUSTRIES, COMPANY_SIZES, USER_ESTIMATES, LICENSE_OPTIONS, TIMELINES, REGIONS,
} from '../data'

export default function Sales() {
  const [f, setF] = useState({
    name: '', email: '', company: '', region: REGIONS[0], phone: '',
    industry: INDUSTRIES[0], size: COMPANY_SIZES[1], users: USER_ESTIMATES[1],
    license: LICENSE_OPTIONS[1].id, timeline: TIMELINES[1], notes: '',
  })
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }))
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const submit = async (e) => {
    e.preventDefault()
    if (!f.name || !f.email || !f.company) return toast.error('Name, business email and company are required.')
    setBusy(true)
    try {
      const r = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          kind: 'launch',
          name:  f.name,
          email: f.email,
          company: f.company,
          regions: f.region,
          role:    f.industry,
          use_case: `${f.industry} · ${f.size} · ${f.users}`,
          tenants_expected: f.users,
          budget_band: LICENSE_OPTIONS.find((o) => o.id === f.license)?.label,
          timeline: f.timeline,
          notes:    (f.phone ? `phone: ${f.phone}\n` : '') + (f.notes || ''),
        }),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || 'submission failed')
      setDone(true)
      toast.success('Enquiry received. A named account executive will reply within one business day.')
    } catch (err) {
      toast.error(err.message || 'Something went wrong')
    } finally { setBusy(false) }
  }

  return (
    <section id="sales" className="relative py-24 md:py-28 scroll-mt-24">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Sales Enquiry</Eyebrow>
          <SectionHeading align="center"
            title={<>Tell us about your business{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">so we can help meaningfully.</span></>}
            subtitle="A quick qualification form so the first reply from our team already has commercial context. Nothing here is used for marketing spam."
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* LEFT — trust column */}
          <div className="lg:col-span-4 space-y-3">
            {[
              { i: Sparkles,     l: 'Named account executive',       b: 'One human, one thread. No round-robin.' },
              { i: ShieldCheck,  l: 'No marketing spam',              b: 'You receive one commercial reply, and only relevant follow-ups.' },
              { i: Clock,        l: 'Reply within 1 business day',    b: 'Priority routing for Enterprise-band enquiries.' },
            ].map((t) => (
              <div key={t.l} className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-4 flex items-start gap-3">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-brand-gold/35 bg-brand-gold/[0.08] text-brand-gold shrink-0">
                  <t.i strokeWidth={1.8} style={{ width: 15, height: 15 }} />
                </span>
                <div>
                  <div className="text-[13.5px] font-semibold text-white leading-tight">{t.l}</div>
                  <div className="text-[12px] text-zinc-400 leading-relaxed mt-1">{t.b}</div>
                </div>
              </div>
            ))}
            <div className="rounded-2xl border border-brand-gold/25 bg-brand-gold/[0.05] backdrop-blur-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Handshake className="h-4 w-4 text-brand-gold" />
                <span className="text-[10.5px] font-mono uppercase tracking-wider text-brand-gold">Prefer email?</span>
              </div>
              <a href="mailto:sales@kalkiai.trade" className="text-[14px] font-semibold text-white hover:brightness-125">sales@kalkiai.trade</a>
              <div className="text-[11.5px] text-zinc-400 leading-relaxed mt-1">Business enquiries, custom decks and vendor onboarding — straight to the enterprise desk.</div>
            </div>
          </div>

          {/* RIGHT — form */}
          <div className="lg:col-span-8">
            <form onSubmit={submit}
              className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-5 md:p-6 backdrop-blur-xl overflow-hidden">
              <div className="pointer-events-none absolute inset-0 opacity-60"
                style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.08), transparent 70%)' }} />
              {done ? (
                <div className="relative flex flex-col items-center gap-3 py-12 text-center">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-emerald-400/50 bg-emerald-400/[0.12] text-emerald-400">
                    <CheckCircle2 className="h-6 w-6" />
                  </span>
                  <div>
                    <div className="text-[16px] font-semibold text-white">Enquiry received</div>
                    <p className="text-[13px] text-zinc-400 mt-1 max-w-sm">A named account executive will reply within one business day. Watch for a mail from a @kalkiai.trade address.</p>
                  </div>
                </div>
              ) : (
                <div className="relative grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Group icon={UsersRound} label="You">
                    <Field icon={Sparkles} label="Full name"       value={f.name}    onChange={set('name')}   autoComplete="name" />
                    <Field icon={Mail}     label="Business email"  value={f.email}   onChange={set('email')}  autoComplete="email"     type="email" />
                    <Field icon={Phone}    label="Phone (optional)" value={f.phone}   onChange={set('phone')}  autoComplete="tel"       type="tel" />
                    <Field icon={Globe}    label="Region"           value={f.region} onChange={set('region')} isSelect options={REGIONS} />
                  </Group>

                  <Group icon={Building2} label="Company">
                    <Field icon={Building2} label="Company name"    value={f.company}  onChange={set('company')} autoComplete="organization" />
                    <Field icon={Factory}   label="Industry"         value={f.industry} onChange={set('industry')} isSelect options={INDUSTRIES} />
                    <Field icon={UsersRound} label="Company size"    value={f.size}    onChange={set('size')}    isSelect options={COMPANY_SIZES} />
                    <Field icon={Layers}    label="Estimated users"   value={f.users}   onChange={set('users')}   isSelect options={USER_ESTIMATES} />
                  </Group>

                  <Group icon={Tag} label="Interest" span2>
                    <Field icon={Tag}   label="Interested license" value={f.license}  onChange={set('license')}  isSelect options={LICENSE_OPTIONS.map((o) => ({ value: o.id, label: o.label }))} />
                    <Field icon={Clock} label="Preferred launch"    value={f.timeline} onChange={set('timeline')} isSelect options={TIMELINES} />
                  </Group>

                  <div className="md:col-span-2">
                    <label className="block">
                      <div className="flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
                        <MessageCircle className="h-3 w-3 text-brand-gold" /> Message
                      </div>
                      <textarea rows={4} value={f.notes} onChange={(e) => set('notes')(e.target.value)}
                        placeholder="Anything specific you would like us to prepare for the first call? (business goals, current stack, integration needs, procurement constraints…)"
                        className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white outline-none focus:border-brand-gold/50 focus:ring-1 focus:ring-brand-gold/30 transition-colors placeholder-zinc-600 resize-none" />
                    </label>
                  </div>

                  <div className="md:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2">
                    <GradientButton disabled={busy} onClick={submit} href={undefined} size="lg" className="justify-center">
                      {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Handshake className="h-4 w-4" /> Send to sales</>}
                    </GradientButton>
                    <span className="text-[11px] text-zinc-500 leading-relaxed">
                      By submitting you accept our privacy handling. Only routed to the account executive team — never to marketing lists.
                    </span>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </Container>
    </section>
  )
}

function Group({ icon: Icon, label, span2, children }) {
  return (
    <div className={clsx('rounded-xl border border-white/10 bg-white/[0.02] p-3', span2 && 'md:col-span-2')}>
      <div className="flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 mb-2 pb-2 border-b border-white/5">
        <Icon className="h-3 w-3 text-brand-gold" /> {label}
      </div>
      <div className="flex flex-col gap-2">
        {children}
      </div>
    </div>
  )
}

function Field({ icon: Icon, label, value, onChange, type = 'text', autoComplete, isSelect, options }) {
  const optionList = options?.map((o) => (typeof o === 'string' ? { value: o, label: o } : o)) || []
  return (
    <label className="block">
      <div className="flex items-center gap-1.5 text-[10.5px] font-mono uppercase tracking-wider text-zinc-500 mb-1">
        {Icon && <Icon className="h-3 w-3 text-brand-gold" />}
        {label}
      </div>
      {isSelect ? (
        <select value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white outline-none focus:border-brand-gold/50 focus:ring-1 focus:ring-brand-gold/30 transition-colors appearance-none">
          {optionList.map((o) => <option key={o.value} value={o.value} className="bg-zinc-900">{o.label}</option>)}
        </select>
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} autoComplete={autoComplete}
          className="w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white outline-none focus:border-brand-gold/50 focus:ring-1 focus:ring-brand-gold/30 transition-colors placeholder-zinc-600" />
      )}
    </label>
  )
}
